import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useFeedbackContext } from '@/contexts/FeedbackContext'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { ProjectCreateOrEditPendingComponent } from '@/features/admin/projects/create-edit/components/ProjectCreateOrEditPendingComponent'
import { ProjectForm } from '@/features/admin/projects/create-edit/components/ProjectForm'
import { projectFormOptions } from '@/features/admin/projects/create-edit/helper/projectFormOptions'
import { useUpdateProject } from '@/features/admin/projects/create-edit/mutations/useUpdateProject'
import { useProjectCreateOrEditSchema } from '@/features/admin/projects/create-edit/schemas/projectCreateOrEditSchema'
import {
    trackingKindsQueryOptions,
    useTrackingKinds,
} from '@/features/edit-trackings/queries/useTrackingKinds'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { IdentityUserSortBy } from '@/generated/Enumerations/IdentityUserSortBy_pb'
import { IdentityUserFiltersDto } from '@/generated/Messages/Filters_pb'
import { useAppForm } from '@/hooks/useAppForm'
import {
    projectDetailsQueryOptions,
    useProjectDetails,
} from '@/queries/useProjectDetails'
import { usersQueryOptions, useUsers } from '@/queries/useUsers'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

const seleniumPrefix = 'edit-project'

const RouteComponent: React.FC = () => {
    const { id } = Route.useParams()
    const { data: allUsers } = useUsers(usersFilters, true)
    const { data: projectDetails, refetch } = useProjectDetails(
        id,
        DeletedItemSelection.NORMAL_ONLY
    )
    const { t } = useTranslation()
    const updateProject = useUpdateProject()

    const {
        data: { trackingKinds },
    } = useTrackingKinds()

    const projectCreateOrEditSchema = useProjectCreateOrEditSchema()
    const { addFeedback } = useFeedbackContext()
    const form = useAppForm({
        ...projectFormOptions(projectDetails),

        validators: {
            onSubmit: projectCreateOrEditSchema,
        },

        onSubmitInvalid: () => {
            addFeedback?.({
                variant: 'snackbar',
                severity: 'warning',
                title: t('snackbar.failedValidation'),
            })
        },

        onSubmit: async ({ value, formApi }) => {
            await updateProject.mutateAsync({
                ...value,
                oldUsers: projectDetails.users,
            })

            await refetch()

            formApi.reset()
        },
    })

    return (
        <>
            <title>{t('projects.urlEdit')}</title>

            <form.AppForm>
                <form.AdminForm
                    title={t('projects.edit')}
                    backTo={'/admin/projects'}
                >
                    <form.Blocker data-selenium={`${seleniumPrefix}-blocker`} />

                    <ProjectForm
                        form={form}
                        trackingKinds={trackingKinds}
                        users={allUsers.users}
                    />
                </form.AdminForm>
            </form.AppForm>
        </>
    )
}

const usersFilters: Partial<OmitDeepMessageFields<IdentityUserFiltersDto>> = {
    deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
    sorting: [{ sortBy: IdentityUserSortBy.LAST_NAME }],
}

const PendingComponent: React.FC = () => {
    const { t } = useTranslation()
    return <ProjectCreateOrEditPendingComponent title={t('projects.edit')} />
}

export const Route = createFileRoute('/__base-layout/admin/projects/$id/edit')({
    component: RouteComponent,
    beforeLoad: async ({
        params: { id },
        context: { user, queryClient, grpcClients },
    }) => {
        assertUserIsAdminOrRedirect(user)

        await Promise.all([
            queryClient.ensureQueryData(
                projectDetailsQueryOptions(
                    grpcClients,
                    id,
                    DeletedItemSelection.NORMAL_ONLY
                )
            ),
            queryClient.ensureQueryData(trackingKindsQueryOptions(grpcClients)),
            queryClient.ensureQueryData(
                usersQueryOptions(grpcClients, usersFilters, true)
            ),
        ])
    },
    pendingComponent: PendingComponent,
})
